import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { ExportDialogComponent } from '@core/components/export-dialog/export-dialog.component';
import { ExportDialogComponentInjectedData, ExportDialogTypeEnum } from '@core/components/export-dialog/export-dialog.types';
import { Recurrence } from '@core/interfaces';
import { SidenavService } from '@core/services/sidenav.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { merge, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DialogRecurrencesComponent } from './dialog/dialog-recurrences.component';
import { FilterRecurrencesComponent } from './filter/filter-recurrences.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { RecurrenceService } from './services/recurrence.service';

@Component({
  selector: 'recurrences',
  templateUrl: './recurrences.component.html',
  styleUrls: ['./recurrences.component.scss'],
  animations: [fuseAnimations]
})
export class RecurrencesComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(FilterRecurrencesComponent, { static: true }) filterRecurrencesComponent: FilterRecurrencesComponent;
  @ViewChild('datatable', { read: ElementRef, static: true }) datatable: ElementRef<HTMLDivElement>;

  filtered = false;
  totalCount = 0;
  displayedColumns = [
    'location',
    'company',
    'segment',
    'maintenanceType',
    'message',
    'createdAt',
    'edit'
  ];
  recurrences: Recurrence[] = [];
  loading = true;
  subscriptions = new Subject<void>();
  recurrencesReload = new Subject<any>();

  public hasEditRecurrencePermission = this.authenticationService.hasPermission('recurrences-edit');
  public hasCreateRecurrencePermission = this.authenticationService.hasPermission('recurrences-create');
  public hasCreateExportPermission = this.authenticationService.hasPermission('exports-create');

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private fuseProgressBarService: FuseProgressBarService,
    private authenticationService: AuthenticationService,
    private recurrenceService: RecurrenceService,
    public sidenavService: SidenavService,
    private matDialog: MatDialog
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  async ngOnInit(): Promise<void> {
    this.sort.active = 'createdAt';
    this.sort.direction = 'desc';

    merge(
      this.sort.sortChange,
      this.paginator.page,
      this.filterRecurrencesComponent.filterChanged.pipe(
        tap(() => this.paginator.firstPage())
      ),
      this.recurrencesReload
    ).pipe(
      debounceTime(500),
      takeUntil(this.subscriptions),
      switchMap(() => {
        this.fuseProgressBarService.show();
        this.loading = true;

        const options = {
          orderBy: this.sort.direction ? `${this.sort.active}_${this.sort.direction}` : null,
          offset: this.paginator.pageIndex * this.paginator.pageSize,
          limit: this.paginator.pageSize
        };

        return this.recurrenceService.listRecurrences({
          where: this.filterRecurrencesComponent.filterChanged.value,
          options
        });
      })
    ).subscribe((result) => {
      this.datatable.nativeElement.scrollTo({
        top: 0,
      });

      this.recurrences = result.items;
      this.filtered = result.filtered;
      this.totalCount = result.totalCount;
      this.loading = false;
      this.fuseProgressBarService.hide();
    });
  }

  reload(): void {
    this.recurrencesReload.next();
  }

  async getRecurrenceById(recurrenceId?: string): Promise<Recurrence> {
    if (!recurrenceId) {
      return {};
    }

    return this.recurrenceService.getById(recurrenceId);
  }

  async openDialog(recurrenceId?: string): Promise<void> {
    this.fuseProgressBarService.show();

    const recurrence = await this.getRecurrenceById(recurrenceId);

    const dialogRecurrences = this.matDialog.open(DialogRecurrencesComponent, {
      panelClass: 'dialog-recurrences',
      data: {
        recurrence,
        isViewMode: recurrenceId && !this.hasEditRecurrencePermission,
        recurrencesReload: this.recurrencesReload
      }
    });

    this.fuseProgressBarService.hide();

    const reload = await dialogRecurrences.afterClosed().toPromise();
    if (reload) {
      this.reload();
    }
  }

  public openExportDialog(): void {
    this.matDialog.open(ExportDialogComponent, {
      panelClass: 'export-dialog',
      autoFocus: false,
      data: {
        type: ExportDialogTypeEnum.recurrences,
        limit: 10000,
        total: this.totalCount,
        where: this.filterRecurrencesComponent.filterChanged.value
      } as ExportDialogComponentInjectedData
    });
  }

  clearFilters(): void {
    this.filtered = false;
    this.filterRecurrencesComponent.clearFilters();
  }

  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.unsubscribe();
  }
}
